/* eslint-disable @typescript-eslint/no-empty-function */
import { useSession } from '@lendi/lala-react';
import { AnalyticsDestination } from '@lendi/analytics-web';
import { useLDClient, useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState, useRef } from 'react';
import { Analytics } from '../tracking';
import { getEnv } from '../auth/getEnv';
import { v4 as uuidv4 } from 'uuid';

interface AnalyticsInstance {
  trackEvent: any;
  trackPage: any;
  identify: any;
  identifyCalled?: boolean;
}

const initialState = {
  analytics: {
    trackEvent: () => {},
    trackPage: () => {},
    identify: () => {},
    isReady: () => false,
    identifyCalled: false,
  },
};

const VALID_DESTINATIONS: string[] = ['amplitude', 'segment', 'both'];

interface LDState {
  analytics?: AnalyticsInstance;
}
export const LDContext = React.createContext<LDState>(initialState);

const LaunchDarklyConsumer: React.FC = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const flags = useFlags();

  const rawDestination = flags['analyticsDestination'] as string | undefined;
  const destination: AnalyticsDestination =
    rawDestination && VALID_DESTINATIONS.includes(rawDestination)
      ? (rawDestination as AnalyticsDestination)
      : 'amplitude';

  const { identity, brand } = useSession();

  const analyticsRef = useRef<{ instance: Analytics; destination: AnalyticsDestination } | null>(null);
  if (!analyticsRef.current || analyticsRef.current.destination !== destination) {
    analyticsRef.current = {
      instance: new Analytics(destination, getEnv(), brand),
      destination,
    };
  }
  const analyticsInstance = analyticsRef.current.instance;

  if (!localStorage.getItem('ajs_anonymous_id')) {
    localStorage.setItem('ajs_anonymous_id', `"${uuidv4()}"`);
  }
  const [identifyCalled, setIdentifyCalled] = useState(false);
  const targetId = localStorage.getItem('ajs_anonymous_id') as string;
  const ldClient = useLDClient();

  useEffect(() => {
    if (!ldClient) return;
    if (!analyticsInstance?.isReady()) return;
    const custom = { targetId, brand };

    if (identity) {
      ldClient.identify({ key: identity.id, ...custom, anonymous: false }, undefined, (_error) => {
        analyticsInstance?.identify(identity.id, flags || {});
        setIdentifyCalled(true);
      });
    } else {
      ldClient.identify({ anonymous: true, ...custom }, undefined, (_error) => {
        analyticsInstance?.identify('', flags || {});
        setIdentifyCalled(true);
      });
    }
    return () => {
      setIsLoading(false);
    };
    // eslint-disable-next-line
  }, [ldClient, identity, analyticsInstance?.isReady()]);

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1500);
  }, []);
  if (isLoading) return null;
  const analytics = {
    trackEvent: analyticsInstance?.trackEvent.bind(analyticsInstance),
    trackPage: analyticsInstance?.trackPage.bind(analyticsInstance),
    identify: analyticsInstance?.identify.bind(analyticsInstance),
    identifyCalled: identifyCalled,
  };
  return <LDContext.Provider value={{ analytics }}>{children}</LDContext.Provider>;
};
export default LaunchDarklyConsumer;
